import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  useMealsSelection,
  usePlan,
  useStep,
} from "../contexts/SubscriptionContext";
import Icon from "../common/Icon";
import GeneralButton from "../common/GeneralButton";
import { TiChevronLeft, TiChevronRight } from "react-icons/ti";
import mealShadow from "../../assets/images/plate-2.png";
import { useToast } from "../contexts/ToastContext";
import Modal from "../common/Modal";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "../../config/axios";
import FullScreenLoading from "../common/loading/FullScreenLoading";
import ApiErrorMessage from "../common/ApiErrorMessage";
import NoItemsFound from "../common/NoItemsFound";
const useMealsPageContent = () => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const { data } = await axios.get("get-meals");
        if (active) setContent(data);
      } catch (err) {
        console.error(err);
        if (active) setError("Failed to load plans. Please try again later.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return { content, loading, error };
};
const SelectMeals = () => {
  const { content, loading, error } = useMealsPageContent();
  const { selectedPlan, setSelectedPlan } = usePlan();
  const { nextStep, prevStep, goToStep } = useStep();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { selectedMeals, addMeal, removeMeal } = useMealsSelection();
  function isSnack(str) {
    if (!str) return false;

    const snacks = [
      "snack",
      "snacks",
      "afternoon snack",
      "afternoon snacks",
      "afternoon-snacks",
      "afternoon-snack",
      "evening snack",
      "evening-snack",
    ];
    return snacks.some((s) => s.toLowerCase() === str.toLowerCase().trim());
  }
  const isSelected = (mealId) => selectedMeals.some((m) => m._id === mealId);
  const [screen, setScreen] = useState({
    show: false,
    title: "",
    message: "",
  });
  const handleSelect = (meal) => {
    if (isSelected(meal._id)) {
      removeMeal(meal._id);
    } else {
      addMeal(meal);
    }
  };
  const saveToLocal = () => {
    localStorage.setItem("selectedMeals", JSON.stringify(selectedMeals));
  };
  return (
    <>
      {loading && <FullScreenLoading />}
      {error ? (
        <ApiErrorMessage
          message1="Failed to load meals"
          message2="Please check your connection and try again."
        />
      ) : content.length === 0 ? (
        <NoItemsFound
          message1="No meals available"
          message2="Please check back later."
        />
      ) : (
        <motion.div
          initial={{ x: -200, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -200, opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className=""
        >
          <div className="relative">
            <div className="grid grid-cols-1 2.5xl:grid-cols-[5.15fr_0.8fr] ">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between pb-0 md:pb-4 lg:pb-9">
                <p className="text-30pt xl:text-34pt ">Select your meals</p>
                {selectedPlan && (
                  <p className="text-18pt text-quaternary font-mono ">
                    5 meals/ week
                  </p>
                )}
              </div>
            </div>

            <div className="relative mt-4 md:mt-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 1xl:grid-cols-4 2.5xl:grid-cols-[1.4fr_1.4fr_1.4fr_1.4fr_0.8fr] gap-8 md:gap-10 lg:gap-8 items-start">
              {content.map((meal, index) => {
                const selected = isSelected(meal._id);

                // Opacity sequence: 20%, 40%, 60%, 40%
                const opacitySteps = [0.2, 0.4, 0.6, 0.4];
                const opacity = opacitySteps[index % opacitySteps.length];

                const handleClick = () => {
                  if (isSnack(meal.title)) {
                    // Check if any other meal is selected
                    const otherMealsSelected = selectedMeals.some(
                      (m) => !isSnack(m.title) // 👈 use isSnack here too
                    );

                    if (!otherMealsSelected) {
                      setScreen((prev) => ({
                        ...prev,
                        show: true,
                        title: "Add Snack to Plan",
                        message:
                          "To add a snack, you need to include at least one other meal in your plan first.",
                      }));
                      return;
                    }
                  }

                  if (selected) {
                    // Remove the meal
                    removeMeal(meal._id);

                    // After removing, check if Snack is now invalid
                    const mainMealsLeft = selectedMeals.filter(
                      (m) => m._id !== meal._id && !isSnack(m.title)
                    );

                    if (mainMealsLeft.length === 0) {
                      // Remove Snack if it's alone
                      const snack = selectedMeals.find((m) => isSnack(m.title));
                      if (snack) removeMeal(snack._id);
                    }
                  } else {
                    addMeal(meal);
                  }
                };

                return (
                  <div
                    key={index}
                    title="Click to select meal"
                    className={`cursor-pointer rounded-[15px] flex flex-col  items-end ${
                      (index + 1 - 1) % 4 === 0 && index !== 0
                        ? "col-start-1"
                        : ""
                    }`}
                    onClick={handleClick}
                  >
                    <div
                      className={`cursor-pointer w-full rounded-[15px]`}
                      style={{
                        transition:
                          "background-color 0.3s ease, margin 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease",
                      }}
                    >
                      <div
                        className={`rounded-[15px]  pt-8 2.5xl:pt-10 pb-8 relative lg:mb-5 hover:shadow-lg 1xl:hover:scale-105 transform`}
                        style={{
                          backgroundColor: `rgba(41, 91, 36, ${opacity})`,
                          transition:
                            "background-color 0.3s ease, margin 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease",
                        }}
                      >
                        <h2 className="text-40pt xl:text-48pt leading-none px-8 2.5xl:px-12">
                          {isSnack(meal.title) ? "Snacks" : meal?.title}
                        </h2>
                        <h2 className="text-28pt xl:text-32pt leading-none px-8 2.5xl:px-12">
                          meal
                        </h2>
                        <div className="relative  flex items-center justify-center">
                          {/* Tray icon */}
                          {/* <Icon icon="mealTray" className="w-full h-full" /> */}

                          <img
                            src={mealShadow}
                            alt="shadow"
                            className="w-full h-full -translate-x-3 px-6"
                          />

                          {/* Meal image inside tray */}
                          <div className="absolute w-[80%] min-h-[200px] h-auto md:w-[250px] lg:w-[480px] md:h-[250px] lg:h-[480px] xl:w-[240px]  xl:h-[240px] flex items-center justify-center rounded-full overflow-hidden">
                           
                            <img
                              src={
                                process.env.REACT_APP_BACKEND_URL +
                                "public/" +
                                meal.image
                              }
                              onError={(e) => {
                                e.currentTarget.src = require("../../assets/images/link.png");
                              }}
                              alt={meal.title}
                              className="w-[80%]  h-auto md:w-[250px] md:max-w-[240px] lg:w-[70%] xl:w-[250px] max-w-[250px]   object-contain  absolute"
                            />
                          </div>

                          {/* Price badge button */}
                          <GeneralButton
                            text={
                              <>
                                <span className="capitalize">AED</span>{" "}
                                {meal.price?.$numberDecimal}/meal
                              </>
                            }
                            icon={null}
                            bgColor="#295B24"
                            className="xl:text-18pt lg:text-14pt md:text-18pt text-18pt absolute bottom-0 left-1/2 -translate-x-1/2"
                            textColor="#fff"
                            textType="lowercase"
                          />
                        </div>

                        <Icon
                          icon={selected ? "checked" : "checkbox"}
                          className={
                            selected
                              ? " w-[41.7px] h-[36px] xs:w-[51.7px] xs:h-[46px] absolute bottom-8 right-[10.5px]   2.5xl:right-[23.5px]"
                              : " w-[30px] h-[30px] xs:w-[42px] xs:h-[42px] absolute bottom-8 right-4 2.5xl:right-8"
                          }
                        />
                      </div>
                      {isSnack(meal.title) && (
                        <div className={`rounded-[15px] px-2 py-2 `}>
                          <p
                            className="text-20pt md:text-21pt font-mono w-[95%] md:w-[90%] mx-auto"
                            dangerouslySetInnerHTML={{
                              __html: `<p>Snacks can only be added as part of a breakfast, lunch, or combined meal, and are not available separately</p>`,
                            }}
                          ></p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className=" mt-5 lg:mt-5 2xl:absolute bottom-24 left-0 gap-5 xs:gap-8 grid xs:grid-cols-4 md:grid-cols-4 sm:mt-4 xl:grid-cols-4 2.5xl:grid-cols-[1.4fr_1.4fr_1.4fr_1.4fr_0.8fr] w-full">
              <div className="flex  items-center justify-center xs:justify-start">
                <GeneralButton
                  text={"Prev step"}
                  title=""
                  onClick={() => {
                    prevStep();
                    navigate(-1);
                  }}
                  iconPosition="Left"
                  bgColor="#ccc"
                  textColor="#000"
                  icon={<TiChevronLeft size={24} className="ms-0 md:me-4" />}
                  className="text-21pt md:text-24pt"
                />
              </div>
              <div className="hidden xs:block"></div>
              <div className="hidden xs:block"></div>
              <div className="flex  items-center justify-center xs:justify-end">
                <GeneralButton
                  onClick={() => {
                    if (selectedMeals.length === 0) {
                      setScreen((prev) => ({
                        ...prev,
                        show: true,
                        title: "Select a Meal",
                        message: "Please select at least one meal to continue.",
                      }));
                    } else {
                      saveToLocal();
                      if (isLoggedIn) {
                        goToStep("3.2");
                        navigate("/subscribe/child");
                      } else {
                        nextStep();
                        navigate("/subscribe/signin");
                      }
                    }
                  }}
                  text={"Next step"}
                  title=""
                  bgColor="#30160E"
                  textColor="#ccc"
                  icon={<TiChevronRight size={24} className="ms-0 md:ms-4" />}
                  className="text-21pt md:text-24pt"
                />
              </div>
            </div>
          </div>
          <Modal
            isOpen={screen.show}
            onClose={() =>
              setScreen((prev) => ({
                ...prev,
                show: false,
              }))
            }
            title={screen.title}
            onConfirm={() =>
              setScreen((prev) => ({
                ...prev,
                show: false,
              }))
            }
            confirmButton="Ok"
            dismissible={false}
            showCancel={false}
          >
            <div className="font-mono">
              <p>{screen.message}</p>
            </div>
          </Modal>
        </motion.div>
      )}
    </>
  );
};

export default SelectMeals;
